import { PASSIVE_TREES, SPELLS, SPELL_ORDER } from "../data";
import { escapeHtml } from "../net-ui";
import { spellIconInner, synergyOrbInner } from "../spell-icons";
import { PATCH_RELEASES } from "./releases";
import {
  PATCH_NOTE_CATEGORY_LABELS,
  type PatchRelease,
} from "./types";

function passiveNode(spellId, passiveId) {
  return (PASSIVE_TREES[spellId] || []).find(n => n.id === passiveId);
}

function parseHexColor(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Lighten very dark spell colors so the patch-notes left accent stays visible on the panel bg. */
function patchNotesSpellAccent(color) {
  const rgb = parseHexColor(color);
  if (!rgb) return color;
  const lum = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  if (lum >= 0.2) return color;
  const mix = (c, target) => Math.round(c + (target - c) * 0.55);
  return `rgb(${mix(rgb.r, 180)}, ${mix(rgb.g, 150)}, ${mix(rgb.b, 220)})`;
}

function formatInlineBold(text) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  let html = "";
  for (let i = 0; i < parts.length; i++) {
    const chunk = escapeHtml(parts[i] ?? "");
    html += i % 2 === 1 ? `<b>${chunk}</b>` : chunk;
  }
  return html;
}

/** Supports `**bold**` and `\n` line breaks within one patch-note line. */
function formatPatchText(text) {
  return text.split("\n").map(formatInlineBold).join("<br>");
}

function spellSortIndex(spellId) {
  const i = SPELL_ORDER.indexOf(spellId);
  return i >= 0 ? i : 999;
}

function createPassiveRef(spellId, passiveId) {
  const n = passiveNode(spellId, passiveId);
  if (!n) return null;
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "pn-passive-ref" + (n.synergy ? " pn-passive-ref-synergy" : "");
  btn.setAttribute("data-tip-kind", "passive");
  btn.setAttribute("data-tip-spell", spellId);
  btn.setAttribute("data-tip-id", passiveId);
  const orb = document.createElement("span");
  orb.className = "pn-passive-orb";
  orb.setAttribute("aria-hidden", "true");
  if (n.synergy) orb.innerHTML = synergyOrbInner(passiveId, n.name);
  btn.appendChild(orb);
  const label = document.createElement("span");
  label.className = "pn-passive-name";
  label.textContent = n.name;
  btn.appendChild(label);
  if (n.synergy) {
    btn.classList.add("pn-passive-ref-previewable");
    btn.setAttribute("data-synergy-preview", passiveId);
  }
  if (n.synergy && n.requiresSpell && SPELLS[n.requiresSpell]) {
    const pair = document.createElement("span");
    pair.className = "pn-partner-icon spell-icon";
    pair.setAttribute("aria-hidden", "true");
    pair.style.background = `${SPELLS[n.requiresSpell].color}33`;
    pair.style.border = `1px solid ${SPELLS[n.requiresSpell].color}`;
    pair.innerHTML = spellIconInner(n.requiresSpell, SPELLS[n.requiresSpell].name);
    btn.appendChild(pair);
  }
  return btn;
}

function createSpellHead(spellId) {
  const s = SPELLS[spellId];
  const head = document.createElement("div");
  head.className = "pn-spell-head";
  if (!s) {
    head.textContent = spellId;
    return head;
  }
  head.style.setProperty("--pn-spell-color", patchNotesSpellAccent(s.color));
  head.setAttribute("data-tip-kind", "spell");
  head.setAttribute("data-tip-id", spellId);
  head.setAttribute("data-tip-lvl", "1");
  if (window.SpellPreview?.has?.(spellId)) {
    head.classList.add("pn-spell-head-previewable");
    head.setAttribute("data-spell-preview", spellId);
  }
  const icon = document.createElement("div");
  icon.className = "pn-spell-icon spell-icon";
  icon.style.background = `${s.color}33`;
  icon.style.border = `1px solid ${s.color}`;
  icon.innerHTML = spellIconInner(spellId, s.name);
  head.appendChild(icon);
  const meta = document.createElement("div");
  meta.className = "pn-spell-meta";
  const name = document.createElement("span");
  name.className = "pn-spell-name";
  name.textContent = s.name;
  meta.appendChild(name);
  head.appendChild(meta);
  return head;
}

function createChangeRow(spellId, change) {
  const li = document.createElement("li");
  li.className = "pn-change";
  if (change.passiveId) {
    const ref = createPassiveRef(spellId, change.passiveId);
    if (ref) li.appendChild(ref);
  }
  const text = document.createElement("span");
  text.className = "pn-change-text";
  text.innerHTML = formatPatchText(change.text);
  li.appendChild(text);
  return li;
}

function createSpellGroup(group) {
  const block = document.createElement("div");
  block.className = "pn-spell";
  block.dataset.spellId = group.spellId;
  const s = SPELLS[group.spellId];
  if (s?.color) block.style.setProperty("--pn-spell-color", patchNotesSpellAccent(s.color));
  block.appendChild(createSpellHead(group.spellId));
  const list = document.createElement("ul");
  list.className = "pn-change-list";
  for (const change of group.changes) list.appendChild(createChangeRow(group.spellId, change));
  block.appendChild(list);
  return block;
}

function createGameplaySection(release) {
  const entries = release.gameplay;
  if (!entries.length) return null;
  const section = document.createElement("section");
  section.className = "pn-section pn-section-gameplay";
  section.id = `pn-gameplay-${release.id}`;
  const title = document.createElement("h3");
  title.className = "pn-section-title";
  title.textContent = PATCH_NOTE_CATEGORY_LABELS.gameplay;
  section.appendChild(title);
  const ul = document.createElement("ul");
  ul.className = "pn-general-list";
  for (const entry of entries) {
    const li = document.createElement("li");
    li.innerHTML = formatPatchText(entry.text);
    ul.appendChild(li);
  }
  section.appendChild(ul);
  return section;
}

function createSkillsSection(release) {
  const groups = release.skills;
  if (!groups.length) return null;
  const section = document.createElement("section");
  section.className = "pn-section pn-section-skills";
  section.id = `pn-skills-${release.id}`;
  const title = document.createElement("h3");
  title.className = "pn-section-title";
  title.textContent = PATCH_NOTE_CATEGORY_LABELS.skills;
  section.appendChild(title);
  const list = document.createElement("div");
  list.className = "pn-spell-list";
  const sorted = [...groups].sort((a, b) => spellSortIndex(a.spellId) - spellSortIndex(b.spellId));
  for (const group of sorted) list.appendChild(createSpellGroup(group));
  section.appendChild(list);
  return section;
}

function createCategoryNav(release) {
  const hasGameplay = release.gameplay.length > 0;
  const hasSkills = release.skills.length > 0;
  if (!hasGameplay || !hasSkills) return null;
  const nav = document.createElement("nav");
  nav.className = "pn-cat-nav";
  nav.setAttribute("aria-label", "Patch categories");
  if (hasGameplay) {
    const a = document.createElement("a");
    a.className = "pn-cat-link";
    a.href = `#pn-gameplay-${release.id}`;
    a.textContent = "Gameplay";
    nav.appendChild(a);
  }
  if (hasSkills) {
    const a = document.createElement("a");
    a.className = "pn-cat-link";
    a.href = `#pn-skills-${release.id}`;
    a.textContent = "Skills";
    nav.appendChild(a);
  }
  return nav;
}

function createVersionSelect(activeId, onChange) {
  if (PATCH_RELEASES.length <= 1) return null;
  const wrap = document.createElement("div");
  wrap.className = "pn-version-picker";
  const label = document.createElement("label");
  label.className = "pn-version-label";
  label.htmlFor = "pn-version-select";
  label.textContent = "Version";
  const select = document.createElement("select");
  select.id = "pn-version-select";
  select.className = "pn-version-select";
  select.setAttribute("aria-label", "Patch version");
  for (const release of PATCH_RELEASES) {
    const opt = document.createElement("option");
    opt.value = release.id;
    opt.textContent = `${release.version} — ${release.date}`;
    if (release.id === activeId) opt.selected = true;
    select.appendChild(opt);
  }
  select.addEventListener("change", () => onChange(select.value));
  wrap.appendChild(label);
  wrap.appendChild(select);
  return wrap;
}

function renderRelease(release) {
  const article = document.createElement("article");
  article.className = "pn-release";
  article.dataset.releaseId = release.id;
  article.dataset.version = release.version;

  const header = document.createElement("header");
  header.className = "pn-release-header";
  const version = document.createElement("h2");
  version.className = "pn-version";
  version.textContent = release.version;
  header.appendChild(version);
  const date = document.createElement("time");
  date.className = "pn-date";
  date.textContent = release.date;
  header.appendChild(date);
  article.appendChild(header);

  const catNav = createCategoryNav(release);
  if (catNav) article.appendChild(catNav);

  const gameplay = createGameplaySection(release);
  if (gameplay) article.appendChild(gameplay);
  const skills = createSkillsSection(release);
  if (skills) article.appendChild(skills);

  return article;
}

function wirePatchNotesPreview(container) {
  if (!container) return;
  delete container.dataset.spellPreviewWired;
  window.SpellPreview?.wireCatalog?.(container);
}

/** Fill `#patchNotesBody` (or any container). Shows one release; defaults to newest. */
export function renderPatchNotes(container, selectedId) {
  if (!container) return;
  const activeId = selectedId ?? PATCH_RELEASES[0]?.id;

  container.innerHTML = "";

  const releaseWrap = document.createElement("div");
  releaseWrap.className = "pn-release-wrap";

  const showRelease = (id) => {
    const release = PATCH_RELEASES.find(r => r.id === id) ?? PATCH_RELEASES[0];
    releaseWrap.replaceChildren();
    if (release) releaseWrap.appendChild(renderRelease(release));
    wirePatchNotesPreview(container);
  };

  const picker = createVersionSelect(activeId, showRelease);
  if (picker) container.appendChild(picker);
  container.appendChild(releaseWrap);
  showRelease(activeId);
}

export function latestPatchRelease() {
  return PATCH_RELEASES[0];
}
